<script setup>
import { getCurrentInstance, toRefs, ref } from 'vue';
import { useRoute } from 'vue-router';
import { DriverStore } from '../pinia/Driver.js';

const { proxy } = getCurrentInstance();
const Driver = DriverStore();
const route = useRoute();

const props = defineProps({
  data: Object,
  crewmanage: Boolean,
  request: Boolean,
  change: Boolean,
  changebis: Boolean,
  event: String
});
const { data, crewmanage, request, event, change, changebis } = toRefs(props);

const emit = defineEmits(['refreshmembers', 'closeDialog']);
const refreshmembers = () => emit('refreshmembers');
const closeDialog = () => emit('closeDialog');

const dialog = ref(false);
const driverToDelete = ref(null);
const errorMessage = ref('');

async function action(type) {
  const res = await proxy.$api({
    serviceName: "crew",
    methodName: type,
    parameters: [route.params.id, data.value.personaID, ""]
  });
}

async function accept() {
  const res = await proxy.$api({
    serviceName: "crew",
    methodName: "acceptjoin",
    parameters: [data.value.personaID, route.params.id]
  });
  refreshmembers();
}

async function reject() {
  const res = await proxy.$api({
    serviceName: "crew",
    methodName: "rejectjoin",
    parameters: [data.value.personaID, route.params.id]
  });
  refreshmembers();
}

async function admin() {
  var value = "0";
  if (data.value.canManage == "0") value = "1";

  const res = await proxy.$api({
    serviceName: "crew",
    methodName: "canmanage",
    parameters: [route.params.id, data.value.personaID, value]
  });
  refreshmembers();
}

function changePersona() {
  Driver.setEasharpptr_p(data.value.ID);
  GetUserInfo();
  closeDialog();
}

async function GetUserInfo() {
  const res = await proxy.$api({
    serviceName: "session",
    methodName: "GetUserInfo"
  });

  Driver.setPersona(res.persona);
  Driver.setUser(res.user);
  Driver.setCrew(res.crew);
  Driver.setCurrentCrew(res.crew.current);
  Driver.setOthers(res.others);
  Driver.setLogged(true);
}

function confirmDelete(id) {
  driverToDelete.value = id;
  errorMessage.value = ''; // Reset error message
  dialog.value = true;
}

async function deleteDriver() {
  if (driverToDelete.value) {
    try {
      const res = await proxy.$api({
        serviceName: "account",
        methodName: "deletePersona",
        parameters: [driverToDelete.value]
      });
      if (res === "Persona deleted successfully.") {
        refreshmembers();
        dialog.value = false;
      } else {
        errorMessage.value = res;
      }
    } catch (error) {
      if (error.response && error.response.data) {
        errorMessage.value = error.response.data;
      } else {
        errorMessage.value = "An error occurred while deleting the driver.";
      }
    }
  }
}

function getRoleIcon(role) {
  const roleIcons = {
    'STAFF': 'mdi-shield-account',
    'PREMIUM': 'mdi-crown',
    'DEVELOPER': 'mdi-wrench',
    'CONTENT_CREATOR': 'mdi-star'
  };
  return roleIcons[role] || 'mdi-account';
}
</script>

<template>
  <div class="driver-card-wrapper">
    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="dialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="dialog-title">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Confirm Deletion
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4">
          <v-alert v-if="errorMessage" type="error" variant="tonal">
            {{ errorMessage }}
          </v-alert>
          <div v-else class="confirmation-text">
            Are you sure you want to delete this driver? This action cannot be undone.
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="justify-end pa-4">
          <v-btn @click="dialog = false" variant="outlined" color="primary">
            Cancel
          </v-btn>
          <v-btn @click="deleteDriver" color="error" variant="elevated">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Driver Card -->
    <div 
      v-if="data.reward != 1" 
      class="driver-card"
      :class="{ 'with-reward': data.reward == 1, 'current-driver': changebis }"
    >
      <!-- Avatar -->
      <div class="driver-avatar">
        <img 
          :src="'https://cdn.nightriderz.world/images/website/icon-persona/' + data.iconIndex + '.jpg'" 
          :alt="data.name"
        >
        <div v-if="data.level && !crewmanage" class="driver-level" :class="data.role">
          <span class="level-number">{{ data.level }}</span>
          <svg class="level-icon" :class="data.role" v-html="levelIcon(data.level)"></svg>
        </div>
      </div>

      <!-- Driver Info -->
      <div class="driver-info">
        <router-link :to="'/driver/' + data.name" class="driver-link">
          <h4 class="driver-name" :class="data.role" :style="data.color ? 'color:' + data.color : ''">
            <span v-if="data.tag" class="driver-tag">[{{ data.tag }}]</span>
            {{ data.name }}
          </h4>
        </router-link>

        <div class="driver-details">
          <div v-if="data.xmpp" class="detail-item">
            <v-icon size="14">mdi-account</v-icon>
            {{ data.xmpp }}
          </div>
          <div v-if="data.points" class="detail-item">
            <v-icon size="14">mdi-clock-outline</v-icon>
            Joined <timeago :datetime="data.date_join" />
          </div>
          <div v-if="change && data.first_login" class="detail-item">
            <v-icon size="14">mdi-calendar</v-icon>
            Created at <timeago :datetime="data.first_login" />
          </div>
          <div v-if="data.points" class="detail-item">
            <v-icon size="14">mdi-star</v-icon>
            {{ number_format(data.points) }} points
          </div>
          <div v-if="data.contributor" class="detail-item">
            <v-icon size="14">mdi-trophy</v-icon>
            {{ number_format(data.contributor) }} {{ event }}
          </div>
        </div>

        <!-- Role Badge -->
        <div v-if="data.role && data.role !== 'FREEMIUM'" class="role-badge" :class="data.role">
          <v-icon size="small">{{ getRoleIcon(data.role) }}</v-icon>
        </div>
      </div>

      <!-- Actions -->
      <div class="driver-actions">
        <!-- Crew Management Buttons -->
        <div v-if="crewmanage" class="action-buttons">
          <v-btn 
            size="small"
            @click="admin()" 
            title="Toggle Admin" 
            icon="mdi-shield-crown-outline"
            :color="data.canManage == '1' ? 'primary' : ''"
            variant="tonal"
          ></v-btn>
          <v-btn 
            size="small"
            @click="action('Kick')" 
            title="Kick" 
            icon="mdi-hand-wave"
            variant="tonal"
            :disabled="data.personaID == Driver.personaID"
          ></v-btn>
          <v-btn 
            size="small"
            @click="action('Ban')" 
            title="Ban" 
            icon="mdi-gavel"
            color="error"
            variant="tonal"
            :disabled="data.personaID == Driver.personaID"
          ></v-btn>
        </div>

        <!-- Request Buttons -->
        <div v-if="request" class="action-buttons">
          <v-btn 
            size="small"
            @click="accept" 
            title="Accept" 
            icon="mdi-checkbox-marked-circle-outline"
            color="success"
            variant="tonal"
          ></v-btn>
          <v-btn 
            size="small"
            @click="reject" 
            title="Decline" 
            icon="mdi-close-circle-outline"
            color="error"
            variant="tonal"
          ></v-btn>
        </div>

        <!-- Change/Delete Buttons -->
        <div v-if="change" class="action-buttons">
          <v-btn 
            size="small"
            @click="changePersona" 
            title="Switch to this driver" 
            icon="mdi-swap-horizontal"
            color="primary"
            variant="tonal"
          ></v-btn>
          <v-btn 
            size="small"
            @click="confirmDelete(data.ID)" 
            title="Delete driver" 
            icon="mdi-delete"
            color="error"
            variant="tonal"
          ></v-btn>
        </div>

        <!-- Current Driver Badge -->
        <div v-if="changebis" class="current-driver-badge">
          <v-icon size="20" color="primary">mdi-check-circle</v-icon>
          <span>Current</span>
        </div>
      </div>
    </div>

    <!-- Card with Reward (duplicate with border) -->
    <div 
      v-if="data.reward == 1" 
      class="driver-card with-reward"
      :class="{ 'current-driver': changebis }"
    >
      <!-- Same content as above -->
      <div class="driver-avatar">
        <img 
          :src="'https://cdn.nightriderz.world/images/website/icon-persona/' + data.iconIndex + '.jpg'" 
          :alt="data.name"
        >
        <div v-if="data.level && !crewmanage" class="driver-level" :class="data.role">
          <span class="level-number">{{ data.level }}</span>
          <svg class="level-icon" :class="data.role" v-html="levelIcon(data.level)"></svg>
        </div>
      </div>

      <div class="driver-info">
        <router-link :to="'/driver/' + data.name" class="driver-link">
          <h4 class="driver-name" :class="data.role" :style="data.color ? 'color:' + data.color : ''">
            <span v-if="data.tag" class="driver-tag">[{{ data.tag }}]</span>
            {{ data.name }}
          </h4>
        </router-link>

        <div class="driver-details">
          <div v-if="data.xmpp" class="detail-item">
            <v-icon size="14">mdi-account</v-icon>
            {{ data.xmpp }}
          </div>
          <div v-if="data.points" class="detail-item">
            <v-icon size="14">mdi-clock-outline</v-icon>
            Joined <timeago :datetime="data.date_join" />
          </div>
          <div v-if="change && data.first_login" class="detail-item">
            <v-icon size="14">mdi-calendar</v-icon>
            Created at <timeago :datetime="data.first_login" />
          </div>
          <div v-if="data.points" class="detail-item">
            <v-icon size="14">mdi-star</v-icon>
            {{ number_format(data.points) }} points
          </div>
          <div v-if="data.contributor" class="detail-item">
            <v-icon size="14">mdi-trophy</v-icon>
            {{ number_format(data.contributor) }} {{ event }}
          </div>
        </div>

        <div v-if="data.role && data.role !== 'FREEMIUM'" class="role-badge" :class="data.role">
          <v-icon size="small">{{ getRoleIcon(data.role) }}</v-icon>
        </div>
      </div>

      <div class="driver-actions">
        <div v-if="crewmanage" class="action-buttons">
          <v-btn 
            size="small"
            @click="admin()" 
            title="Toggle Admin" 
            icon="mdi-shield-crown-outline"
            :color="data.canManage == '1' ? 'primary' : ''"
            variant="tonal"
          ></v-btn>
          <v-btn 
            size="small"
            @click="action('Kick')" 
            title="Kick" 
            icon="mdi-hand-wave"
            variant="tonal"
            :disabled="data.personaID == Driver.personaID"
          ></v-btn>
          <v-btn 
            size="small"
            @click="action('Ban')" 
            title="Ban" 
            icon="mdi-gavel"
            color="error"
            variant="tonal"
            :disabled="data.personaID == Driver.personaID"
          ></v-btn>
        </div>

        <div v-if="request" class="action-buttons">
          <v-btn 
            size="small"
            @click="accept" 
            title="Accept" 
            icon="mdi-checkbox-marked-circle-outline"
            color="success"
            variant="tonal"
          ></v-btn>
          <v-btn 
            size="small"
            @click="reject" 
            title="Decline" 
            icon="mdi-close-circle-outline"
            color="error"
            variant="tonal"
          ></v-btn>
        </div>

        <div v-if="change" class="action-buttons">
          <v-btn 
            size="small"
            @click="changePersona" 
            title="Switch to this driver" 
            icon="mdi-swap-horizontal"
            color="primary"
            variant="tonal"
          ></v-btn>
          <v-btn 
            size="small"
            @click="confirmDelete(data.ID)" 
            title="Delete driver" 
            icon="mdi-delete"
            color="error"
            variant="tonal"
          ></v-btn>
        </div>

        <!-- Current Driver Badge -->
        <div v-if="changebis" class="current-driver-badge">
          <v-icon size="20" color="primary">mdi-check-circle</v-icon>
          <span>Current</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.driver-card-wrapper {
  width: 100%;
}

.driver-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  width: 100%;
  margin-bottom: 16px;
}

.driver-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(var(--v-theme-primary), 0.3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.driver-card.with-reward {
  border: 2px solid #73AD21;
  background: rgba(115, 173, 33, 0.05);
}

.driver-card.current-driver {
  border-color: rgba(var(--v-theme-primary), 0.5);
  background: rgba(var(--v-theme-primary), 0.08);
}

.driver-card.current-driver:hover {
  border-color: rgba(var(--v-theme-primary), 0.7);
}

.driver-avatar {
  position: relative;
  flex-shrink: 0;
}

.driver-avatar img {
  width: 64px;
  height: 64px;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  object-fit: cover;
}

.driver-level {
  position: absolute;
  bottom: -6px;
  right: -6px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(26, 26, 46, 0.95);
  padding: 4px 8px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 0.8em;
  font-weight: 700;
}

.level-number {
  line-height: 1;
}

.level-icon {
  width: 16px;
  height: 16px;
}

.driver-info {
  flex: 1;
  min-width: 0;
  position: relative;
}

.driver-link {
  text-decoration: none;
}

.driver-name {
  margin: 0 0 8px 0;
  font-size: 1.1em;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 6px;
}

.driver-tag {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

.driver-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.6);
}

.detail-item .v-icon {
  opacity: 0.6;
}

.role-badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.driver-actions {
  flex-shrink: 0;
}

.action-buttons {
  display: flex;
  gap: 6px;
  align-items: center;
}

.action-buttons .v-btn {
  min-width: auto !important;
}

.current-driver-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(var(--v-theme-primary), 0.2);
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-primary), 0.3);
  font-size: 0.85em;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

/* Role-specific colors */
.PREMIUM,
.driver-level.PREMIUM {
  color: #d9be00;
  fill: #d9be00;
}

.FREEMIUM,
.driver-level.FREEMIUM {
  color: #0054a5;
  fill: #0054a5;
}

.STAFF,
.driver-level.STAFF {
  color: rgb(173, 74, 255);
  fill: rgb(173, 74, 255);
}

.role-badge.STAFF {
  background: rgba(173, 74, 255, 0.2);
  color: rgb(173, 74, 255);
  border: 1px solid rgba(173, 74, 255, 0.3);
}

.DEVELOPER,
.driver-level.DEVELOPER {
  color: rgb(17, 252, 74);
  fill: rgb(17, 252, 74);
}

.role-badge.DEVELOPER {
  background: rgba(17, 252, 74, 0.2);
  color: rgb(17, 252, 74);
  border: 1px solid rgba(17, 252, 74, 0.3);
}

.CONTENT_CREATOR,
.driver-level.CONTENT_CREATOR {
  color: rgb(255, 5, 33);
  fill: rgb(255, 5, 33);
}

.role-badge.CONTENT_CREATOR {
  background: rgba(255, 5, 33, 0.2);
  color: rgb(255, 5, 33);
  border: 1px solid rgba(255, 5, 33, 0.3);
}

.role-badge.PREMIUM {
  background: rgba(217, 190, 0, 0.2);
  color: #d9be00;
  border: 1px solid rgba(217, 190, 0, 0.3);
}

/* Dialog styles */
.dialog-title {
  font-size: 1.2em;
  font-weight: 600;
  padding: 20px;
}

.confirmation-text {
  font-size: 1em;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
}

/* Responsive */
@media (max-width: 600px) {
  .driver-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .driver-actions {
    width: 100%;
  }

  .action-buttons {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
